/**
 * LeakDetector — tracks Three.js GPU objects via FinalizationRegistry + WeakRef.
 *
 * Registers Texture, BufferGeometry, Material instances and detects when they
 * become unreachable without dispose() being called.
 * Only active when __DEV__ is true (tree-shaken in production).
 */

import { Logger } from '../core/Logger.js';

declare const __DEV__: boolean;

interface TrackedEntry {
  label: string;
  type: string;
  registeredAt: number;
  ref: WeakRef<object>;
}

const MAX_LEAKED = 500;

export class LeakDetector {
  private alive = new Map<number, TrackedEntry>();
  /** Reverse lookup: object → id for O(1) markDisposed */
  private objToId = new WeakMap<object, number>();
  private leaked: TrackedEntry[] = [];
  private nextId = 0;
  private registry: FinalizationRegistry<number> | null = null;
  private _disposed = false;

  constructor() {
    // Opt-in: only a bundler that DEFINES __DEV__ = true turns tracking on. The
    // previous guard treated an undefined __DEV__ (the tsc ESM build, the
    // esbuild fat bundle) as "dev" and tracked every asset in production.
    if (typeof __DEV__ === 'undefined' || !__DEV__) return;
    if (typeof FinalizationRegistry === 'undefined') {
      Logger.warn('LeakDetector', 'FinalizationRegistry not available — leak detection disabled');
      return;
    }

    this.registry = new FinalizationRegistry((id: number) => {
      // After dispose(), the registry may still fire callbacks for any
      // previously-registered object that gets GC'd. Guard so the callback
      // doesn't push into cleared state and corrupt a second-run detector's
      // diagnostics.
      if (this._disposed) return;
      const entry = this.alive.get(id);
      if (entry) {
        if (this.leaked.length < MAX_LEAKED) this.leaked.push(entry);
        this.alive.delete(id);
      }
    });
  }

  /**
   * Register an object to track for leaks.
   */
  register(obj: object, label: string): void {
    if (!this.registry) return;

    // If already registered, remove old entry to prevent orphaned alive entries
    const existingId = this.objToId.get(obj);
    if (existingId !== undefined) {
      this.alive.delete(existingId);
      this.registry.unregister(obj);
    }

    const id = this.nextId++;
    const entry: TrackedEntry = {
      label,
      type: obj.constructor.name,
      registeredAt: performance.now(),
      ref: new WeakRef(obj),
    };
    this.alive.set(id, entry);
    this.objToId.set(obj, id);
    // Pass obj as the unregister token (3rd arg) so markDisposed can
    // call unregister() to cancel the callback. Without this token,
    // FinalizationRegistry.unregister() is a no-op and disposed objects
    // would be falsely reported as leaks.
    this.registry.register(obj, id, obj);
  }

  /**
   * Mark an object as properly disposed (removes from tracking).
   */
  markDisposed(obj: object): void {
    if (!this.registry) return;

    const id = this.objToId.get(obj);
    if (id !== undefined) {
      this.alive.delete(id);
      this.objToId.delete(obj);
      this.registry.unregister(obj);
    }
  }

  /**
   * Get count of currently alive (tracked) objects.
   * Counts only entries whose WeakRef is still valid.
   */
  getAliveCount(): number {
    let count = 0;
    for (const entry of this.alive.values()) {
      if (entry.ref.deref()) count++;
    }
    return count;
  }

  /**
   * Get list of alive objects with labels.
   */
  getAlive(): { label: string; type: string; ageMs: number }[] {
    const now = performance.now();
    const result: { label: string; type: string; ageMs: number }[] = [];
    for (const entry of this.alive.values()) {
      if (entry.ref.deref()) {
        result.push({
          label: entry.label,
          type: entry.type,
          ageMs: now - entry.registeredAt,
        });
      }
    }
    return result;
  }

  /**
   * Get suspected leaks (GC'd without dispose).
   */
  getLeaked(): { label: string; type: string }[] {
    return this.leaked.map(e => ({ label: e.label, type: e.type }));
  }

  /**
   * Print a console table of alive objects and suspected leaks.
   */
  report(): void {
    const alive = this.getAlive();
    const leaked = this.getLeaked();

    if (alive.length > 0) {
      Logger.info('LeakDetector', `Alive objects: ${alive.length}\n${JSON.stringify(alive, null, 2)}`);
    }

    if (leaked.length > 0) {
      Logger.warn('LeakDetector', `Suspected leaks: ${leaked.length}\n${JSON.stringify(leaked, null, 2)}`);
    }

    if (alive.length === 0 && leaked.length === 0) {
      Logger.info('LeakDetector', 'No tracked objects');
    }
  }

  dispose(): void {
    this._disposed = true;
    this.alive.clear();
    this.leaked.length = 0;
    this.nextId = 0;
    // Drop the registry so pending GC callbacks can't push into stale state.
    // `_disposed` in the callback also guards against the race where a
    // callback is already scheduled when dispose() runs.
    this.registry = null;
  }
}
